import { useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Game } from "@/types/game"
import { ListType } from "@/types/game"
import { canRegister, getGamePath } from "@/utils/game.utils"
import { Icon } from "@/components/ui/icon"

interface GameRegistrationButtonProps {
  game: Game
  user?: { id: string } | null
  userTeamRole?: string | null
  isRegistered?: boolean
  variant?: "default" | "mobile"
  className?: string
  onConfirm?: () => void // Callback customizado para quando o botão é clicado (útil para páginas de confirmação)
  currentParticipantsCount?: number // Número atual de participantes confirmados (para verificar se está cheio)
  /** Quando true, mostra botão desabilitado "Verificando vagas..." até ter o count (evita ir para checkout quando lista está cheia) */
  participantsLoading?: boolean
  /** Oculta o texto de apoio abaixo do botão (barra mobile já exibe). */
  hideHint?: boolean
}

export function GameRegistrationButton({
  game,
  user,
  userTeamRole,
  isRegistered = false,
  variant = "default",
  className = "",
  onConfirm,
  currentParticipantsCount,
  participantsLoading = false,
  hideHint = false,
}: GameRegistrationButtonProps) {
  const navigate = useNavigate()

  // Verificar se o jogo já passou (encerrado)
  const now = new Date()
  const gameDate = new Date(game.gameDate)
  const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null
  const gameEnded = gameEndDate ? gameEndDate < now : gameDate < now

  // Verificar se o usuário pode se registrar
  const userCanRegister = user && !isRegistered ? canRegister(game, {
    userId: user.id,
    userTeamRole: userTeamRole || undefined,
  }) : false

  // Calcular data de início para exibição
  const getStartDate = (): string => {
    if (game.listType === ListType.DIRECT) {
      if (game.listOpenAt) {
        const date = new Date(game.listOpenAt)
        const dateStr = date.toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        const timeStr = date.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        })
        return `${dateStr} às ${timeStr}`
      }
    } else if (game.listType === ListType.INTEREST) {
      if (game.interestStartAt) {
        const date = new Date(game.interestStartAt)
        const dateStr = date.toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        const timeStr = date.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        })
        return `${dateStr} às ${timeStr}`
      }
    }
    return ""
  }

  const startDate = getStartDate()

  // Função para navegar para a página correta
  const handleRegistration = () => {
    // Se há um callback customizado, usar ele ao invés de navegar
    if (onConfirm) {
      onConfirm()
      return
    }
    
    const listRevealAt = game.listRevealAt ? new Date(game.listRevealAt) : null
    const isInterestBeforeReveal =
      game.listType === ListType.INTEREST && (!listRevealAt || now < listRevealAt)

    if (isInterestBeforeReveal) {
      navigate(getGamePath(game, "interest"))
      return
    }

    // Verificar se a lista está cheia
    const isFull = game.maxParticipants > 0 && currentParticipantsCount !== undefined && currentParticipantsCount >= game.maxParticipants
    
    if (isFull) {
      if (game.allowWaitlist) {
        navigate(getGamePath(game, "waiting"))
      } else {
        navigate(getGamePath(game))
      }
      return
    }

    if (game.pricePerPlayer === 0) {
      navigate(getGamePath(game, "register"))
      return
    }

    navigate(getGamePath(game, "checkout"))
  }

  // Se o usuário já está registrado, não mostrar nada
  if (isRegistered) {
    return null
  }

  // Se o usuário não está logado, não mostrar nada
  if (!user) {
    return null
  }

  // Se o jogo já encerrou, mostrar apenas um aviso
  if (gameEnded) {
    return (
      <div className={`${variant === "mobile" ? "" : ""} w-full bg-amber-50 dark:bg-amber-900/10 p-4 rounded-lg border border-amber-200 dark:border-amber-900/20`}>
        <div className="flex items-start gap-3">
          <Icon name="info" />
          <div className="flex-1">
            <p className="text-foreground text-sm font-medium mb-1">Jogo Encerrado</p>
            <p className="text-muted-foreground text-xs">Este jogo já foi encerrado e não aceita mais inscrições.</p>
          </div>
        </div>
      </div>
    )
  }

  // Verificar se a lista está cheia (quando participantsLoading, não sabemos - desabilitar botão)
  const isFull = game.maxParticipants > 0 && currentParticipantsCount !== undefined && currentParticipantsCount >= game.maxParticipants

  const listRevealAt = game.listRevealAt ? new Date(game.listRevealAt) : null
  const isInterestBeforeReveal =
    game.listType === ListType.INTEREST && (!listRevealAt || now < listRevealAt)

  // Determinar o texto do botão
  let buttonText = isInterestBeforeReveal ? "Quero Participar" : "Realizar Inscrição"
  
  // Se a lista estiver cheia
  if (isFull) {
    if (!game.allowWaitlist) {
      buttonText = "Vagas Esgotadas";
    } else {
      buttonText = "Entrar na Lista de Espera";
    }
  }

  // Estilos baseado na variante
  // Usando cn para combinar classes de forma inteligente (permite sobrescrever via props)
  const buttonClassName = cn(
    "text-base font-bold shadow-lg shadow-primary/25 rounded-xl w-full",
    variant === "mobile" && "mb-2",
    className
  )
  
  // Para container, não aplicar className (já foi mesclado no botão)
  const containerClassName = ""
  
  // Se temos classes customizadas de tamanho, não usar size padrão do Button se for conflitante
  // Mas como estamos usando cn, o tailwind-merge deve resolver a maioria dos conflitos
  // Mantemos o padrão do size para garantir o estilo base se não houver override
  const buttonSize = "default"

  // Verificar se o botão deve estar desabilitado
  const isRegisterDisabled = isFull && !game.allowWaitlist
  const isVerifyingSlots =
    participantsLoading ||
    (game.maxParticipants > 0 && currentParticipantsCount === undefined)

  if (userCanRegister) {
    return (
      <div className={containerClassName}>
        <Button 
          className={buttonClassName}
          onClick={handleRegistration}
          size={buttonSize}
          disabled={isRegisterDisabled || isVerifyingSlots}
        >
          {isVerifyingSlots ? "Verificando vagas..." : buttonText}
        </Button>
                    {!hideHint && !isRegisterDisabled && (
                      <p className="text-center text-muted-foreground text-sm mt-4">Confirme sua participação no jogo.</p>
                    )}
                    {!hideHint && isRegisterDisabled && (
                      <p className="text-center text-red-500 text-sm mt-4 font-medium">Não há vagas disponíveis para este jogo.</p>
                    )}
      </div>
    )
  }

  // Se não pode se registrar, mostrar "Em Breve" com data abaixo
  return (
    <div className={containerClassName}>
      <Button 
        className={buttonClassName}
        disabled
        size={buttonSize}
      >
        Em Breve
      </Button>
      {startDate && (
        <div className="flex items-center justify-center gap-1.5 mt-4">
          <Icon name="schedule" />
          <p className={`text-center text-muted-foreground ${variant === "mobile" ? "text-xs" : "text-sm"}`}>
            <span className="font-semibold">Início:</span> {startDate}
          </p>
        </div>
      )}
    </div>
  )
}
